import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExpertFile } from '@techspert-io/expert-files';
import { Subject, from } from 'rxjs';
import {
  filter,
  finalize,
  map,
  mergeMap,
  share,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  Add,
  Delete,
  ExpertFilesStateService,
  UpdateProgress,
} from '../../../../../shared/services/filestore-state.service';
import { FileStoreService } from '../../../../../shared/services/filestore.service';

@Component({
  selector: 'app-screener-btn',
  template: `@if (screenerFiles.length) {
    @if (downloads$ | async; as downloads) {
      <button
        mat-icon-button
        [matTooltip]="
          'Download ' + (viewStage === 'completed' ? 'call' : 'screener files')
        "
        (click)="handleDownload($event, downloads.active)"
      >
        <span>
          @if (downloads.active) {
            <div>{{ downloads.progress }}%</div>
          }
        </span>
        <mat-icon>
          {{
            downloads.active
              ? 'downloading'
              : downloadFinished
                ? 'download_done'
                : 'download'
          }}
        </mat-icon>
      </button>
    }
  }`,
  styles: [
    `
      button {
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
      }

      span {
        color: #333;
        padding-top: 6px;
      }

      mat-icon {
        color: #b4becb;
      }
    `,
  ],
  providers: [ExpertFilesStateService],
  standalone: false,
})
export class ExpertScreenerBtnComponent implements OnInit, OnDestroy {
  @Input() fill = '#B4BECB';
  @Input() files: ExpertFile[];
  @Input() viewStage: string;

  private destroy$ = new Subject();
  private state$ = this.filesState.state$.pipe(
    share(),
    takeUntil(this.destroy$)
  );
  downloads$ = this.state$.pipe(
    map((p) => this.screenerFiles.map((f) => p[f.fileKey])),
    map((files) => ({
      progress: files.some((p) => p?.progress)
        ? Math.min(...files.map((p) => p?.progress).filter(Boolean))
        : 0,
      active: files.some(Boolean),
    }))
  );
  screenerFiles?: ExpertFile[];
  downloadFinished: boolean;

  constructor(
    private fileStore: FileStoreService,
    private filesState: ExpertFilesStateService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.viewStage === 'completed') {
      this.screenerFiles = (this.files || []).filter((f) =>
        ['zoomRecording', 'zoomTranscript'].includes(f.type)
      );
    } else {
      this.screenerFiles = (this.files || []).filter(
        (f) => f.type === 'screenerResponse'
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
  }

  handleDownload(e: Event, active: boolean): void {
    e.preventDefault();
    e.stopPropagation();

    active ? this.cancelDownload() : this.downloadFiles();
  }

  private downloadFiles(): void {
    from(this.screenerFiles)
      .pipe(
        tap((file) => this.filesState.dispatch(new Add(file.fileKey))),
        mergeMap((file) =>
          this.fileStore
            .downloadExpertFile(
              file.fileKey,
              `${file.fileName}.${file.fileExtension}`
            )
            .pipe(
              takeUntil(this.state$.pipe(filter((d) => !d[file.fileKey]))),
              tap((event) =>
                this.filesState.dispatch(
                  new UpdateProgress(file.fileKey, event.percentageComplete)
                )
              ),
              finalize(() => {
                this.downloadFinished = true;
                this.filesState.dispatch(new Delete(file.fileKey));
              })
            )
        )
      )
      .subscribe();
  }

  private cancelDownload(): void {
    this.screenerFiles.forEach((f) =>
      this.filesState.dispatch(new Delete(f.fileKey))
    );
  }
}
